/*
 * Copyright (C) 2023 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { DEFAULT_INTERVALL_TIMER, isConflict, isNotNil, ProblemDetail } from '@common/tech-shared';
import { SnackBarService } from '@ui';
import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { isUndefined } from 'lodash-es';
import { of } from 'rxjs';
import { catchError, delay, filter, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { CommandSnackbarService } from '../command-snackbar.service';
import { COMMAND_ERROR_MESSAGES, CommandErrorMessage, CREATE_COMMAND_MESSAGE_BY_ORDER } from '../command.message';
import { CommandListResource, CommandResource, CreateCommandActionProps } from '../command.model';
import { CommandRepository } from '../command.repository';
import { hasCommandError, isConcurrentModification, isPending, isRevokeable } from '../command.util';
import {
  CommandActions,
  CommandProps,
  CreateCommandFailureProps,
  LoadCommandListProps,
  PollCommandProps,
  SnackBarProps,
  UpdateCreateCommandProps,
} from './command.actions';

@Injectable()
export class CommandEffects {
  private readonly actions$ = inject(Actions);
  private readonly repository = inject(CommandRepository);
  private readonly commandSnackbarService = inject(CommandSnackbarService);
  private readonly snackBarService = inject(SnackBarService);

  loadCommandList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommandActions.loadCommandList),
      switchMap((props: LoadCommandListProps) =>
        this.repository.getCommands(props.resource, props.linkRel).pipe(
          map((commandList: CommandListResource) => props.successAction(commandList)),
          catchError((error: HttpErrorResponse) => of(props.failureAction(error.error))),
        ),
      ),
    ),
  );

  createCommand$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommandActions.createCommand),
      switchMap((props: CreateCommandActionProps) =>
        this.repository.createCommand(props.resource, props.linkRel, props.command).pipe(
          map((command: CommandResource) => CommandActions.updateCreateCommand({ createCommandActionProps: props, command })),
          catchError((error) =>
            of(CommandActions.createCommandFailure({ createCommandActionProps: props, error, command: props.command })),
          ),
        ),
      ),
    ),
  );

  updateCreateCommand$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommandActions.updateCreateCommand),
      filter((action) => !isPending(action.command)),
      switchMap((props: UpdateCreateCommandProps) =>
        this.handleCreateCommandSuccess(props.createCommandActionProps, props.command),
      ),
    ),
  );

  handleCreateCommandSuccess(createCommandActionProps: CreateCommandActionProps, command: CommandResource): Action<string>[] {
    if (isRevokeable(command))
      return [
        CommandActions.createCommandSuccess({ createCommandActionProps, command }),
        CommandActions.showRevokeSnackbar({ command }),
      ];

    if (hasCommandError(command)) return this._handleCommandError(command, createCommandActionProps);

    if (this._getSnackBarMessage({ createCommandActionProps, command }))
      return [
        CommandActions.createCommandSuccess({ createCommandActionProps, command }),
        CommandActions.showSnackbar({ command, createCommandActionProps }),
      ];

    return [CommandActions.createCommandSuccess({ createCommandActionProps, command })];
  }

  _handleCommandError(command: CommandResource, createCommandActionProps: CreateCommandActionProps): Action[] {
    if (isConcurrentModification(command.errorMessage))
      return this.handleConcurrentModificationError(command, createCommandActionProps);

    return [
      CommandActions.showErrorSnackbar({ command, createCommandActionProps }),
      CommandActions.createCommandFailure({ createCommandActionProps, command, error: command.errorMessage }),
    ];
  }

  handleConcurrentModificationError(command: CommandResource, createCommandActionProps: CreateCommandActionProps): Action[] {
    return [
      CommandActions.showErrorSnackbar({
        command,
        createCommandActionProps: { ...createCommandActionProps, snackBarErrorMessage: undefined },
      }),
      CommandActions.publishConcurrentModificationAction(),
      CommandActions.createCommandFailure({ createCommandActionProps, command, error: command.errorMessage }),
    ];
  }

  showRevokeSnackbar$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CommandActions.showRevokeSnackbar),
        tap((props: CommandProps) =>
          this.commandSnackbarService.show(props.command, CREATE_COMMAND_MESSAGE_BY_ORDER[props.command.order], true),
        ),
      ),
    { dispatch: false },
  );

  showSnackbar$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CommandActions.showSnackbar),
        tap((props: SnackBarProps) => this.commandSnackbarService.show(props.command, this._getSnackBarMessage(props))),
      ),
    { dispatch: false },
  );

  showErrorSnackbar$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CommandActions.showErrorSnackbar),
        tap((props: SnackBarProps) => this.commandSnackbarService.show(props.command, this._getSnackBarErrorMessage(props))),
      ),
    { dispatch: false },
  );

  showCreateCommandConflictSnackbar$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CommandActions.createCommandFailure),
        filter((props: CreateCommandFailureProps) => this._isConflictProblemDetail(props.error)),
        tap((props: CreateCommandFailureProps) => this.snackBarService.showError((<ProblemDetail>props.error).detail)),
      ),
    { dispatch: false },
  );

  _isConflictProblemDetail(error: unknown): boolean {
    return isConflict((<ProblemDetail>error)?.status) && isNotNil((<ProblemDetail>error)?.detail);
  }

  _getSnackBarErrorMessage(props: SnackBarProps): string {
    return (
      props.createCommandActionProps.snackBarErrorMessage ??
      COMMAND_ERROR_MESSAGES[props.command.errorMessage] ??
      COMMAND_ERROR_MESSAGES[CommandErrorMessage.GENERIC_ERROR]
    );
  }

  _getSnackBarMessage(props: SnackBarProps): string {
    return isUndefined(props.createCommandActionProps.snackBarMessage) ?
        CREATE_COMMAND_MESSAGE_BY_ORDER[props.command.order]
      : props.createCommandActionProps.snackBarMessage;
  }

  revokeCommand$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommandActions.revokeCommand),
      switchMap((props: CommandProps) =>
        this.repository.revokeCommand(props.command).pipe(
          mergeMap((command: CommandResource) => this.handleRevokeCommand(command)),
          catchError((error) => of(CommandActions.revokeCommandFailure({ command: props.command, error }))),
        ),
      ),
    ),
  );

  pollRevokedCommand$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommandActions.pollRevokedCommand),
      delay(DEFAULT_INTERVALL_TIMER),
      switchMap((props: PollCommandProps) =>
        this.repository.getCommand(props.command).pipe(
          mergeMap((command: CommandResource) => this.handleRevokeCommand(command)),
          catchError((error) => of(CommandActions.revokeCommandFailure({ command: props.command, error }))),
        ),
      ),
    ),
  );

  handleRevokeCommand(command: CommandResource): (CommandProps & Action<string>)[] {
    if (isPending(command)) {
      return [CommandActions.pollRevokedCommand({ command })];
    }
    return [CommandActions.revokeCommandSuccess({ command })];
  }
}
